/**
 * Notifications API client for Iteration 6 - Notifications & Sharing.
 */

import apiClient from './client';
import type { 
  Notification, 
  NotificationsPage, 
  UnreadCountResponse, 
  ShareLinkResponse 
} from '../types/notification';

/**
 * Get paginated notifications for the current user.
 * 
 * @param page - Page number (0-indexed)
 * @param size - Page size
 * @returns Promise with paginated notifications
 */
export const getNotifications = async (
  page: number = 0, 
  size: number = 10
): Promise<NotificationsPage> => {
  const response = await apiClient.get<NotificationsPage>('/notifications', {
    params: { page, size },
  });
  return response.data;
};

/**
 * Get unread notification count for the current user.
 * 
 * @returns Promise with unread count
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<UnreadCountResponse>('/notifications/unread-count');
  return response.data.count;
};

/**
 * Mark a notification as read.
 * 
 * @param notificationId - Notification ID
 * @returns Promise with updated notification
 */
export const markAsRead = async (notificationId: string): Promise<Notification> => {
  const response = await apiClient.put<Notification>(`/notifications/${notificationId}/read`);
  return response.data;
};

/**
 * Mark all notifications as read for the current user.
 * 
 * @returns Promise with count of notifications marked as read
 */
export const markAllAsRead = async (): Promise<number> => {
  const response = await apiClient.put<UnreadCountResponse>('/notifications/mark-all-read');
  return response.data.count;
};

/**
 * Delete all notifications for the current user.
 * 
 * @returns Promise that resolves when all notifications are deleted
 */
export const deleteAll = async (): Promise<void> => {
  await apiClient.delete('/notifications');
};

/**
 * Get share link for a post.
 * 
 * @param postId - Post ID
 * @returns Promise with share link response
 */
export const getShareLink = async (postId: string): Promise<ShareLinkResponse> => {
  const response = await apiClient.get<ShareLinkResponse>(`/posts/${postId}/share-link`);
  return response.data;
};
